import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { StorageManager } from '../common/storage-manager.class';

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  configureApp(): void {
    const user = StorageManager.getUser();
    const appName = environment.appName;
    const appType = environment.features.applicationType;

    this.applyDocumentTitle(appName);
    this.setDefaultFeatures(user, appName, appType);
    this.overrideFeaturesForRoles(user, appName, appType);
    this.overrideFeaturesForSpecificClients(user, appName);
    this.overrideCostFeatures(appName);
  }

  /** 
    * Apply title based on environment config
    */
  private applyDocumentTitle(appName: string): void {
    if (appName.toUpperCase() === 'MELIA') {
      document.title = 'FITOGEST BASIC';
    }else{
      document.title = (environment.appName || '').toUpperCase();
    }
  }

  private setDefaultFeatures(user: any, appName: string, appType: string): void {
    if (appName === 'donjardin') {
      environment.features.clientesMostrarTodosDefault = true;
    } else if (appName === 'agrosalvi') {
      environment.features.clientesMostrarTodosDefault = false;
    } else {
      environment.features.clientesMostrarTodosDefault = user?.rol === 'admin' && appName !== 'torrepacheco';
    }

    if (appType === 'viverogest') {
      environment.features.hasPlantas = true;
    }

    if (appType === 'gardengest') {
      environment.features.activateSIEX = false;
      environment.features.activateREA = false;
    }

    if ((appType === 'viverogest' || appType === 'agrogest' || appType === 'cropgest') && appName !== 'benibal') {
      environment.features.showDesplazamientoObra = false;
      environment.features.showConsultaRopo = appName === 'avafitogest';
      environment.features.mostrarTodosTratamientos = true;
      environment.features.showTratamientosAllProductos = true;
    }
  }

  private overrideFeaturesForRoles(user: any, appName: string, _appType: string): void {
    if (user?.tipo === 'comunero') {
      environment.features.showUsuariosFirma = false;
      environment.features.showCostes = true;
      environment.features.userCanSeeCostes = true;
      environment.features.showStock = true;
    }

    if (user?.rol === 'responsable' && appName !== 'benibal' && appName !== 'avafitogest') {
      environment.features.showCargaPAC = false;
      environment.features.canAddClients = false;
      environment.features.canAddFincas = false;
      environment.features.canDeleteFincas = false;
      environment.features.canAddSectores = false;
      environment.features.canDeleteSectores = false;
      environment.features.canAddParcelas = false;
      environment.features.canDeleteParcelas = false;
      environment.features.showTareasFitosanitariasCampaign = true;
      environment.features.showTareasFertilizantesCampaign = true;
      environment.features.showTareasOtrasCampaign = true;
      environment.features.showStock = true;
    }

    if (user?.rol === 'aplicador') {
      environment.features.hasFincas = false;
      if (appName !== 'detorres') {
        environment.features.showMapas = false;
      } else {
        environment.features.showTratamientosMenu = false;
      }
      environment.features.showVentas = false;
      environment.features.showVademecumMenu = false;
      environment.features.hasProductos = false;
      environment.features.showVisitas = false;
      environment.features.showCostes = false;
      environment.features.showStock = false;
      environment.features.showAplicadoresMenu = false;

      if (appName === 'ava') {
        environment.features.showMapas = true;
        environment.features.showMapaParcelas = false;
        environment.features.showVentusky = false;
        environment.features.showMapaTratamientos = true;
      }

      if (appName === 'tomega') {
        environment.features.showAnalisis = false;
        environment.features.showTratamientosMenu = false;
        environment.features.userCanSeeClientes = false;
        environment.features.showTareasFitosanitarias = false;
        environment.features.showTareasFertilizantes = false;
        window.location.href = `${environment.serverUrl}#/dashboard/tareas-otros`;
      }
    }

    if (user?.rol === 'basico' && appName === 'sirga') {
      environment.features.hasFincas = false;
      environment.features.showMapas = false;
      environment.features.showVentas = false;
      environment.features.showVademecumMenu = false;
      environment.features.hasProductos = false;
      environment.features.showVisitas = false;
      environment.features.showCostes = false;
      environment.features.showStock = false;
      environment.features.showTratamientosMenu = false;
      environment.features.showTareasFitosanitarias = false;
      environment.features.showTareasFertilizantes = false;
      environment.features.showTrabajadores = false;
      window.location.href = `${environment.serverUrl}#/dashboard/fichajes`;
    }

    if (user?.rol === 'user' && appName === 'servalbert') {
      environment.features.showDesplazamientoObra = false;
      environment.features.showVentas = false;
    }
  }

  private overrideFeaturesForSpecificClients(user: any, appName: string): void {
    //MES QUE JARDINS - VERSION GARDENGEST
    /*
        Este hace uso de la version general de gardengest
    */
    if (appName === 'gardengest' && user?.id === '4') {
      environment.features.showProductosFeritilizantes = false;
      environment.features.showTareasFertilizantes = false;
      environment.features.showAnalisis = false;
      environment.features.showCuadernoDeCampo = false;
      environment.features.showCargaPAC = false;
      environment.features.showMapas = false;
      environment.features.showHistoricoFT = false;
      environment.features.showEnviarWhatsapp = false;
    }

    if (appName === 'gruesnadal') {
      const favIcon: HTMLLinkElement = document.querySelector('#appIcon') ?? new HTMLLinkElement();

      favIcon.href = 'https://gruesnadal.com/favicon.ico';
    }

    if (appName === 'agrosalvi') {
      if (user?.tipo === 'Garden') {
        environment.features.fincaName = 'Dirección';
        environment.features.fincasName = 'Direcciones';
        environment.features.parcelaName = 'Unidad';
        environment.features.parcelasName = 'Unidades';
        environment.features.showMostrarTodosClientes = true;
      } else if (user?.tipo === 'Agro') {
        environment.features.showMostrarTodosClientes = true;
      }
    }

    if (appName === 'melia' && user?.nombre === 'agrogibe') {
      environment.features.showReto = true;
    }
  }

  private overrideCostFeatures(appName: string): void {
    if (!environment.features.showCostes) {return;};

    environment.features.precioHora = true;
    environment.features.showTrabajadoresCargos = true;
    environment.features.showCostesOtros = true;
    environment.features.showCostesProductos = true;
    environment.features.showCostesTareasFertiCsv = true;
    environment.features.showCostesTareasOtrasCsv = true;
    environment.features.showFormatHourMinute = true;
    environment.features.showTareasFitoHoras = true;
    environment.features.showTareasOtrosDuracion = true;
    environment.features.timeStartStopTareas = true;
    environment.features.tareasHorasTotales = true;

    if (appName === 'tomega') {
      environment.features.horaFechaInicioActualPorDefecto = true;
    } else {
      environment.features.horaFechaFinActualPorDefecto = true;
    }

    environment.features.showTareasFitoLitrosAplicados = true;
    environment.features.showTrabajadoresFertilizantes = true;
    environment.features.showStockAdditionalFields = true;
  }
}
